import React from "react";
import { useTranslation } from "react-i18next";

import { byDefinedKeys } from "../../../../commons/libs/filter-guards";
import { filterSpecGroups } from "../../../../commons/libs/specs";
import { getSpecConfig } from "../../../../commons/specs";
import { Product, ProductKey, ProductType, SpecGroupKeyMap } from "../../../../commons/specs/product";
import * as icons from "../../../../resources/icons";
import Icon from "../../components/Icon/Icon";
import IconLink from "../../components/IconLink/IconLink";
import SpecsItem from "../../components/SpecsList/SpecsItem";
import SpecsList from "../../components/SpecsList/SpecsList";
import SpecsListLayout from "../../components/SpecsListLayout/SpecsListLayout";
import TabBarOverlay from "../../components/TabBarOverlay/TabBarOverlay";
import { generatePath, useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import { useMediaQuery } from "../../libs/hooks/use-media-query";
import useOnMount from "../../libs/hooks/use-on-mount";
import useSpecFormatter from "../../libs/hooks/use-spec-formatter";
import { useSettings } from "../../libs/queries/use-settings";
import { ROUTES } from "../../routes";
import ProductTitlePartial from "../partials/ProductTitlePartial";
import { useTrackingContext } from "../utils/tracking-context";

interface Props {
  product: Product;
}

const SpecificationsOverlay = ({ product }: Props) => {
  const { t } = useTranslation(["commons"]);
  const history = useAppHistory();
  const location = useAppLocation();
  const breakpoints = useMediaQuery();
  const isMobile = !breakpoints.l;
  const { mixpanel } = useTrackingContext();
  const settings = useSettings();

  useOnMount(() => {
    mixpanel?.trackProductSpecificationsOpened(product);

    return () => {
      mixpanel?.trackProductSpecificationsClosed(product);
    };
  });

  const handleLinkClick = (_event: React.MouseEvent<HTMLElement>, href: string) => {
    history.replace(href, location?.state);
  };

  const productType = product[ProductKey.ProductType];
  const specConfig = getSpecConfig(productType);
  const specFormatter = useSpecFormatter("commons", productType, settings.initialized.customization);

  const filteredSpecGroups = filterSpecGroups(
    specConfig.specGroups,
    settings.initialized.customization.showPrices || settings.initialized.customization.showPricesFeaturesOnly
  );

  return (
    <TabBarOverlay
      headerLeft={
        <IconLink
          size={isMobile ? "s" : "base"}
          href={generatePath(ROUTES.PRODUCT_DETAILS.INDEX, { id: product[ProductKey.ProductId] })}
          icon={<Icon source={icons.IconSmallAngleDown} />}
          onClick={handleLinkClick}
          classNames={["u-extended-click-area"]}
        >
          {isMobile
            ? t("commons:specificationsOverlayProductDetails.closeButton")
            : t("commons:specificationsOverlayProductDetails.closeHeadlineButton", {
                headline: t("commons:specificationsOverlayProductDetails.headline")
              })}
        </IconLink>
      }
      headerCenter={
        <ProductTitlePartial
          product={product}
          customization={settings.initialized.customization}
          currency={settings.initialized.currency}
          assortmentPriceSettings={settings.assortmentPriceSettings}
        />
      }
    >
      <SpecsListLayout>
        {filteredSpecGroups
          .map(specGroup => {
            // Limitation of TypeScript:
            // https://github.com/microsoft/TypeScript/issues/57388
            const tKey = `commons:specs.${productType}.specGroups.${specGroup.key}` as keyof {
              [T in ProductType as `commons:specs.${T}.specGroups.${SpecGroupKeyMap[T]}`]: null;
            };
            return (
              <SpecsList headline={t(tKey)} key={specGroup.key}>
                {specGroup.specKeys
                  .filter(specKey => specConfig.specDefinitions[specKey]?.enable?.specOverlay !== false)
                  .map(specKey => ({
                    specKey,
                    label: specFormatter?.formatLabel(specKey),
                    value: specFormatter?.formatProductValue(specKey, product)
                  }))
                  .filter(byDefinedKeys("value", "label"))
                  .map(({ specKey, label, value }) => (
                    <SpecsItem key={String(specKey)} label={label}>
                      {value}
                    </SpecsItem>
                  ))}
              </SpecsList>
            );
          })
          .filter(element => element.props.children.length > 0)}
      </SpecsListLayout>
    </TabBarOverlay>
  );
};

export default SpecificationsOverlay;
